import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useParams, Navigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import ToastMessage from '../common/ToastMessage';
import { setBearerToken } from '../../../redux/app/userSlice';
import MultiStepProgressBar from '../PasswordRecovery/MultiStepProgressBar';
import CreateJWToken from '../../shared/JWToken/CreateJWToken';
import Skeleton from 'src/Member/components/footer/Skeleton';
import SecurityQuestion from '../OnBoarding/SecurityQuestion';
import PasswordReset from '../OnBoarding/PasswordReset';
import PasswordSuccess from '../PasswordRecovery/PasswordSuccess';
import RegistrationErrorMsg from '../OnBoarding/RegistrationErrorMsg';
import AlreadyRegisteredErrorMsg from '../OnBoarding/AlreadyRegisteredErrorMsg';
import CustomClientError from '../../errorhandling/CustomClientError';
import { axiosInstance } from '../../../api/axios';
import { HttpSuccessCodes } from '../../errorhandling/HttpErrorCodes';
import APIEndpoints from '../../api/APIEndpoints';
import ApiEncrypt from '../../utils/Encrypt';
import backArrowIcon from 'src/Admin/img/arrow_back.svg';
import * as yup from 'yup';
import './resetpassword.scss';

const ResetPassword = () => {
  const ErrorMessage = ({ message }) => (
    <ToastMessage type="error" title="Reset Password" subTitle={message} />
  );
  const formFields = {
    Oid: '',
    securityQuestionDetails: '',
    othersOid: '',
    email: '',
    SuperUser: false,
    confirmPassword: '',
    password: '',
    PrivacyAccepted: false,
    TermsAccepted: false,
    ToResetPassword: false,
  };
  const dispatch = useDispatch();
  const id = useParams();
  const [selectedQuestionValues, setSelectedQuestionValues] = useState([
    null,
    null,
    null,
  ]);
  const [isButtonDisable, setIsButtonDisable] = useState(true);
  const [userData, setUserData] = useState(formFields);
  const [currentStep, setCurrentStep] = useState(2);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRules, setPasswordRules] = useState({});
  const [serverError, setServerError] = useState(false);
  const [resetPasswordUserDetails, setResetPasswordUserDetails] = useState({});
  const [showSkeletonLoader, setShowSkeletonLoader] = useState(true);
  const [resetPasswordUrlValid, setResetPasswordUrlValid] = useState(false);
  const [isPasswordReseted] = useState(false);
  const [fetchSecurityQuestions, setFetchSecurityQuestions] = useState(false);
  const { encryptData } = ApiEncrypt();

  const securityQuestionschema = yup.object({
    question1: yup
      .object()
      .required('Question selection is required')
      .nullable(),
    question2: yup
      .object()
      .required('Question selection is required')
      .nullable(),
    question3: yup
      .object()
      .required('Question selection is required')
      .nullable(),
    answer1: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(128, 'Answer must not exceed 128 characters')
      .test(
        'Checking answer 1 and 2',
        'Answer should not be same',
        function (value) {
          return value ? value !== this.parent.answer2 : true;
        },
      )
      .test(
        'Checking answer 1 and 3',
        'Answer should not be same',
        function (value) {
          return value ? value !== this.parent.answer3 : true;
        },
      ),
    answer2: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(128, 'Answer must not exceed 128 characters')
      .test(
        'Checking answer 2 and 1',
        'Answer should not be same',
        function (value) {
          return value ? value !== this.parent.answer1 : true;
        },
      )
      .test(
        'Checking answer 2 and 3',
        'Answer should not be same',
        function (value) {
          return value ? value !== this.parent.answer3 : true;
        },
      ),
    answer3: yup
      .string()
      .required('Answer is required')
      .min(4, 'Answer must be at least 4 characters')
      .max(128, 'Answer must not exceed 128 characters')
      .test(
        'Checking answer 3 and 1',
        'Answer should not be same',
        function (value) {
          return value ? value !== this.parent.answer1 : true;
        },
      )
      .test(
        'Checking answer 3 and 2',
        'Answer should not be same',
        function (value) {
          return value ? value !== this.parent.answer2 : true;
        },
      ),
    customQuestion1: yup.string().when('question1', {
      is: (question1) => question1 && question1.value === 'Others',
      then: () =>
        yup
          .string()
          .required('Question is required')
          .min(10, 'Question must be at least 10 characters')
          .max(256, 'Question must not exceed 256 characters')
          .test(
            'Checking Custom Question 1 and 2',
            'Question should not be same',
            function (value) {
              return value ? value !== this.parent.customQuestion2 : true;
            },
          )
          .test(
            'Checking Custom Question 1 and 3',
            'Question should not be same',
            function (value) {
              return value ? value !== this.parent.customQuestion3 : true;
            },
          ),
    }),
    customQuestion2: yup.string().when('question2', {
      is: (question2) => question2 && question2.value === 'Others',
      then: () =>
        yup
          .string()
          .required('Question is required')
          .min(10, 'Question must be at least 10 characters')
          .max(256, 'Question must not exceed 256 characters')
          .test(
            'Checking Custom Question 2 and 1',
            'Question should not be same',
            function (value) {
              return value ? value !== this.parent.customQuestion1 : true;
            },
          )
          .test(
            'Checking Custom Question 2 and 3',
            'Question should not be same',
            function (value) {
              return value ? value !== this.parent.customQuestion3 : true;
            },
          ),
    }),
    customQuestion3: yup.string().when('question3', {
      is: (question3) => question3 && question3.value === 'Others',
      then: () =>
        yup
          .string()
          .required('Question is required')
          .min(10, 'Question must be at least 10 characters')
          .max(256, 'Question must not exceed 256 characters')
          .test(
            'Checking Custom Question 3 and 1',
            'Question should not be same',
            function (value) {
              return value ? value !== this.parent.customQuestion1 : true;
            },
          )
          .test(
            'Checking Custom Question 3 and 2',
            'Question should not be same',
            function (value) {
              return value ? value !== this.parent.customQuestion2 : true;
            },
          ),
    }),
  });

  const Rules = {
    passMinLength: passwordRules.PasswordMinLength,
    passMaxLength: passwordRules.PasswordMaxLength,
    passNumericsReq: passwordRules.NoOfNumericsReqd,
    passSpecialCharReq: passwordRules.NoOfSpecialCharactersReqd,
    passUpperCaseReq: passwordRules.NoOfUpperCaseReqd,
    passLowerCaseReq: true,
  };
  const regexSpecialChar = new RegExp(
    /^((?=.*[-!$%^&*()/_\\+|~='"\]`{}:[;<>?,.@#]){1}).*$/,
  );

  const passwordSchema = yup.object({
    password: yup
      .string()
      .test((value, context) => {
        const { createError } = context;

        if (!value) {
          return createError({
            message: 'The Password field is required',
          });
        } else if (
          Rules.passMaxLength >= 1 &&
          !userData.SuperUser &&
          value.length > Rules.passMaxLength
        ) {
          return createError({
            message: `Enter a maximum of ${Rules.passMaxLength} characters only`,
          });
        } else if (
          Rules.passMinLength >= 1 &&
          value.length < (userData.SuperUser ? 12 : Rules.passMinLength)
        ) {
          return createError({
            message: `Enter a minimum of ${
              userData.SuperUser ? 12 : Rules.passMinLength
            } characters only`,
          });
        } else if (
          Rules.passUpperCaseReq >= 1 &&
          value.match('(?=.*[A-Z])') === null
        ) {
          return createError({
            message: userData?.SuperUser
              ? 'Enter at least one uppercase letter'
              : 'Please consult password rules',
          });
        } else if (
          Rules.passSpecialCharReq >= 1 &&
          !regexSpecialChar.test(value)
        ) {
          return createError({
            message: userData?.SuperUser
              ? 'Enter at least one special character'
              : 'Please consult password rules',
          });
        } else if (
          Rules.passNumericsReq >= 1 &&
          value.match('(?=.*[0-9])') === null
        ) {
          return createError({
            message: userData?.SuperUser
              ? 'Enter at least one numeric letter'
              : 'Please consult password rules',
          });
        } else {
          return true;
        }
      })
      .required('Enter Password'),

    confirmPassword: yup
      .string()
      .required('Enter Confirm Password')
      .oneOf([yup.ref('password')], 'Passwords do not match'),
  });

  const handleQuestionValChange = (option, index) => {
    const newQuestionVals = selectedQuestionValues;
    newQuestionVals[index] = option;
    setSelectedQuestionValues([...newQuestionVals]);
    setIsButtonDisable(false);
  };

  const handleInput = (e, setFieldValue, handleChange) => {
    handleChange(e);
    setUserData({ ...userData, [e.currentTarget.name]: e.currentTarget.value });
    setFieldValue(e.currentTarget.name, e.currentTarget.value);
  };

  const handleFormData = (keyName, val) => {
    setUserData({ ...userData, [keyName]: val });
  };

  const saveSecurityQuestions = async (securityQuestionsPayload) => {
    try {
      const response = await axiosInstance.post(
        APIEndpoints.postSecurityQuestions(),
        securityQuestionsPayload,
      );
      if (
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200 ||
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_201
      ) {
        return new Promise((resolve, reject) => {
          resolve('Success!');
        });
      } else {
        return new Promise((resolve, reject) => {
          reject(
            'We are unable to update security questions. Please try again!',
          );
        });
      }
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  };

  const updateUser = async () => {
    try {
      console.log(
        'user Date Details :::::::::: >>>>>>>>>>> :::::::: ',
        userData,
      );
      const {
        Oid,
        email,
        password,
        SuperUser,
        PrivacyAccepted,
        TermsAccepted,
        ToResetPassword,
      } = userData;

      const payLoad = {
        UpdateUser: {
          Oid: Oid,
          UserID: email,
          Password: encryptData(password, false),
          ToResetPassword: ToResetPassword,
          SuperUser: SuperUser,
          PrivacyAccepted: PrivacyAccepted,
          TermsAccepted: TermsAccepted,
        },
      };
      const response = await axiosInstance.put(
        APIEndpoints.getUpdateAllUserAPIResource(),
        payLoad,
      );
      if (
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200 ||
        response.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_201
      ) {
        localStorage.setItem('pwd-reset', true);
        return new Promise((resolve, reject) => {
          resolve('Success!');
        });
      } else {
        return new Promise((resolve, reject) => {
          reject('We are unable to update the details. Please try again!');
        });
      }
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  };

  const nextStep = () => {
    console.log('User Data :::::::::::: ', userData);
    setCurrentStep(currentStep + 1);
    //setCurrentStep(5);
  };

  const prevStep = () => {
    if (currentStep === 3) {
      setFetchSecurityQuestions(false);
    }
    setCurrentStep(currentStep - 1);
  };

  const handleSave = async () => {
    if (userData.securityQuestionDetails) {
      const {
        question1,
        question2,
        question3,
        answer1,
        answer2,
        answer3,
        customQuestion1,
        customQuestion2,
        customQuestion3,
      } = userData.securityQuestionDetails;

      const securityQuestionsPayload = {
        SecurityQuestions: [
          {
            oid:
              question1['value'] === 'Others'
                ? userData.othersOid
                : question1['value'],
            securityquestion: customQuestion1
              ? customQuestion1
              : question1['label'],
            answer: answer1,
            custom: question1 && question1['value'] === 'Others' ? true : false,
          },
          {
            oid:
              question2['value'] === 'Others'
                ? userData.othersOid
                : question2['value'],
            securityquestion: customQuestion2
              ? customQuestion2
              : question2['label'],
            answer: answer2,
            custom: question2['value'] === 'Others' ? true : false,
          },
          {
            oid:
              question3['value'] === 'Others'
                ? userData.othersOid
                : question3['value'],
            securityquestion: customQuestion3
              ? customQuestion3
              : question3['label'],
            answer: answer3,
            custom: question3['value'] === 'Others' ? true : false,
          },
        ],
        UserID: userData.email,
      };
      saveSecurityQuestions(securityQuestionsPayload)
        .then(() => {
          console.log('Questions Saved:::::::::::::::');
          updateUser()
            .then((res) => {
              setCurrentStep(5);
            })
            .catch((error) => {
              toast.error(
                <ErrorMessage message={error?.response?.data?.message} />,
              );
            });
        })
        .catch((error) => {
          toast.error(
            <ErrorMessage message={error?.response?.data?.message} />,
          );
        });
    }
  };

  const BackButton = () => {
    return currentStep !== 2 && currentStep !== 5 ? (
      <div
        role={'button'}
        className="pe-auto"
        onClick={prevStep}
        tabIndex={0}
        onKeyPress={(event) => {}}
      >
        <img
          className="position-relative mr-3"
          src={backArrowIcon}
          alt="logo"
        />
      </div>
    ) : currentStep === 2 ? (
      <Link to="/admin">
        <img
          className="position-relative mr-3"
          src={backArrowIcon}
          alt="logo"
        />
      </Link>
    ) : null;
  };

  const handleSecurityQuestionsError = (isError) => {
    console.log('handleSecurityQuestionsError :::::', isError);
    setFetchSecurityQuestions(isError);
  };

  const getPasswordRules = async () => {
    //setSkeletonLoader( true );
    const responseConfiguration = await axiosInstance.get(
      APIEndpoints.getAdminConfigResources(),
    );
    const adminConfig = responseConfiguration.data;
    setPasswordRules(adminConfig?.Response?.PasswordRules);
    //setSkeletonLoader( false );
  };

  const getEncryptionKeyRegistration = async () => {
    try {
      const keyStoreResp = await axiosInstance.get(
        APIEndpoints.getEncryptionKeyResources(),
      );
      console.log(
        'getEncryptionKeyRegistration outSide :::::::::::::::::::: >>>>>>>>>> ',
      );
      if (keyStoreResp.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200) {
        console.log(
          'getEncryptionKeyRegistration Inside :::::::::::::::::::: >>>>>>>>>> ',
        );
        getUserResetPasswordDetails();
      }
    } catch (e) {
      console.log('Error in Reset Password >>>> getEncryptionKey  :::::: ', e);
      setServerError(true);
    }
  };

  const getUserResetPasswordDetails = async () => {
    console.log('getUserResetPasswordDetails :::::::::: in ::::::::::::');
    let createJWT;
    setShowSkeletonLoader(true);
    try {
      createJWT = await CreateJWToken.createJWToken('Forgot Password');
      console.log('JWT Token ::::::::::::::   ', CreateJWToken.getJWTToken());
      console.log('JWT Token ::::::::::::::   ', createJWT);
      dispatch(setBearerToken('Bearer ' + CreateJWToken.getJWTToken()));
      //getPasswordRules();
      console.log(
        'URL :::::::::: Reset Password >>>>>>>>>>> ::::::::: ',
        `${APIEndpoints.getVerifyResetPasswordLink()}${id.id}`,
      );
      const responseUserResetPassword = await axiosInstance.get(
        `${APIEndpoints.getVerifyResetPasswordLink()}${id.id}`,
      );
      console.log(
        'responseUserResetPassword::::::::::::  Reset Password  >>>>>>>>>   :::::  ',
        responseUserResetPassword,
      );
      if (
        responseUserResetPassword.status ===
        HttpSuccessCodes.HTTP_SUCCESS_CODE_200
      ) {
        setResetPasswordUserDetails(responseUserResetPassword.data);
        if (responseUserResetPassword?.data?.Response?.IsExpired === 'true') {
          setResetPasswordUrlValid(true);
        }
        //if ( responseUserResetPassword?.data?.Response?.IsRegistered === 'true' ) {
        //setIsPasswordReseted(true);
        //}
      }
      setShowSkeletonLoader(false);
    } catch (e) {
      setShowSkeletonLoader(false);
      setServerError(true);
      console.log('Error in Reset Password URL :::::: ', e);
    }
  };

  const getUserDetails = async () => {
    setShowSkeletonLoader(true);
    try {
      //console.log("resetPasswordUserDetails    ::::::: Reset Password >>>> ::::  ", resetPasswordUserDetails);
      console.log(
        'getUserDetails :::::::::: Reset Password >>>>>>>>>>> ::::::::: ',
        `${APIEndpoints.getUserDetailsOnBoarding()}`,
      );
      const responseGetUserDetails = await axiosInstance.get(
        `${APIEndpoints.getUserDetailsOnBoarding()}/${
          resetPasswordUserDetails.Response.UserEmail
        }`,
      );
      console.log(
        'responseGetUserDetails::::::::::::  Reset Password  >>>>>>>>>   :::::  ',
        responseGetUserDetails,
      );
      if (
        responseGetUserDetails.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200
      ) {
        const {
          UserID,
          Oid,
          SuperUser,
          PrivacyAccepted,
          TermsAccepted,
          ToResetPassword,
        } = responseGetUserDetails.data.Response.User;

        setUserData({
          ...userData,
          Oid: Oid || '',
          email: UserID || '',
          SuperUser: SuperUser || false,
          PrivacyAccepted: PrivacyAccepted || false,
          TermsAccepted: TermsAccepted || false,
          ToResetPassword: ToResetPassword || false,
        });
      }

      setShowSkeletonLoader(false);
    } catch (e) {
      setShowSkeletonLoader(false);
      setServerError(true);
      console.log('Error in getUserDetails :::::: ', e);
    }
  };

  useEffect(() => {
    getEncryptionKeyRegistration();
  }, []);

  useEffect(() => {
    if (
      resetPasswordUserDetails &&
      resetPasswordUserDetails.Response &&
      resetPasswordUserDetails.Response.UserEmail
    ) {
      getUserDetails();
      getPasswordRules();
      //getPageContent();
    }
  }, [resetPasswordUserDetails]);

  useEffect(() => {
    if (userData.securityQuestionDetails) {
      handleSave();
    }
  }, [userData.securityQuestionDetails]);

  if (serverError) {
    return <Navigate to="/admin/server-error" push={true}></Navigate>;
  }

  return (
    <Container>
      {!isPasswordReseted ? (
        !resetPasswordUrlValid ? (
          <>
            <Row className="mb-4">
              <Col className="p-0">
                {/*<div className="page-header d-flex align-items-center">
            <h1 className="register-header mb-2">Setup Security Questions</h1>
          </div>
          <HorizontalDivider /> */}
                {currentStep !== 5 && (
                  <>
                    <div className="page-header d-flex align-items-center">
                      <BackButton />
                      <h1 className="register-header mb-2">Reset Password</h1>
                    </div>
                    <MultiStepProgressBar currentStep={currentStep} />
                  </>
                )}
              </Col>
            </Row>
            {!showSkeletonLoader ? (
              <Row>
                <Col className="p-0">
                  <div className="page-content-container">
                    {currentStep === 2 && (
                      <PasswordReset
                        currentStep={currentStep}
                        nextStep={nextStep}
                        passwordSchema={passwordSchema}
                        passwordRules={passwordRules}
                        showPassword={showPassword}
                        showConfirmPassword={showConfirmPassword}
                        setShowPassword={setShowPassword}
                        setShowConfirmPassword={setShowConfirmPassword}
                        formData={userData}
                        handleInput={handleInput}
                      />
                    )}
                    {currentStep === 3 ? (
                      !fetchSecurityQuestions ? (
                        <SecurityQuestion
                          currentStep={currentStep}
                          nextStep={handleSave}
                          securityQuestionschema={securityQuestionschema}
                          handleQuestionValChange={handleQuestionValChange}
                          selectedQuestionValues={selectedQuestionValues}
                          formData={userData}
                          handleInput={handleInput}
                          isButtonDisable={isButtonDisable}
                          handleFormData={handleFormData}
                          isSave={true}
                          handleSecurityQuestionsError={
                            handleSecurityQuestionsError
                          }
                        />
                      ) : (
                        <CustomClientError
                          ErrorHeading="Something went wrong with request!"
                          ErrorMessage="Please try again."
                        />
                      )
                    ) : null}
                    {currentStep === 5 && <PasswordSuccess currentStep={5} />}
                  </div>
                </Col>
              </Row>
            ) : (
              <Skeleton count={10} />
            )}
          </>
        ) : (
          <RegistrationErrorMsg errorContent="The reset password link you have clicked on has expired. It seems like this page is no longer available." />
        )
      ) : (
        <AlreadyRegisteredErrorMsg errorContent="The reset password link is already used or has expired." />
      )}
      <div className="toastify">
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </Container>
  );
};

export default ResetPassword;
